import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import {
  Sparkles,
  Loader2,
  Wand2,
  FileText,
  Download,
  ArrowRight,
  CheckCircle2,
  Zap,
  DollarSign,
  Calendar,
  Users,
  Target,
  AlertCircle,
  Eye,
  Edit3,
  Save,
  X,
  Package,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';

interface AIQuickSOWProps {
  accessToken: string;
  consultingFirm?: any;
  onClose?: () => void;
}

interface QuickSOWInput {
  clientName: string;
  industry: string;
  projectType: string;
  budgetRange: string;
  additionalContext?: string;
  timeline?: string;
  teamSize?: string;
}

interface GeneratedSOW {
  title: string;
  executiveSummary: string;
  scope: string;
  deliverables: string[];
  assumptions: string[];
  exclusions: string[];
  timeline: string;
  phases: Array<{
    name: string;
    duration: string;
    description: string;
  }>;
  pricing: {
    model: 'hourly' | 'fixed' | 'hybrid';
    estimatedHours: number;
    hourlyRate: number;
    fixedPrice?: number;
    total: number;
  };
  risks: string[];
  successCriteria: string[];
  paymentTerms: string;
}

const INDUSTRIES = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'retail', label: 'Retail / E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'technology', label: 'Technology / SaaS' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government / Public Sector' },
  { value: 'telecom', label: 'Telecommunications' },
  { value: 'energy', label: 'Energy / Utilities' },
  { value: 'transportation', label: 'Transportation / Logistics' },
  { value: 'other', label: 'Other' },
];

const PROJECT_TYPES = [
  { value: 'jira-migration', label: 'Jira Migration / Upgrade' },
  { value: 'jsm-implementation', label: 'Jira Service Management Implementation' },
  { value: 'servicenow-implementation', label: 'ServiceNow Implementation' },
  { value: 'itsm-optimization', label: 'ITSM Process Optimization' },
  { value: 'service-desk-setup', label: 'Service Desk Setup' },
  { value: 'workflow-automation', label: 'Workflow Automation' },
  { value: 'integration-project', label: 'System Integration' },
  { value: 'custom-development', label: 'Custom Development' },
  { value: 'training-enablement', label: 'Training & Enablement' },
  { value: 'health-check', label: 'Health Check / Assessment' },
  { value: 'other', label: 'Other' },
];

const BUDGET_RANGES = [
  { value: 'under-25k', label: 'Under $25,000' },
  { value: '25k-50k', label: '$25,000 - $50,000' },
  { value: '50k-100k', label: '$50,000 - $100,000' },
  { value: '100k-250k', label: '$100,000 - $250,000' },
  { value: '250k-500k', label: '$250,000 - $500,000' },
  { value: 'over-500k', label: 'Over $500,000' },
];

const TIMELINES = [
  { value: '2-4-weeks', label: '2-4 weeks' },
  { value: '1-2-months', label: '1-2 months' },
  { value: '2-3-months', label: '2-3 months' },
  { value: '3-6-months', label: '3-6 months' },
  { value: '6-12-months', label: '6-12 months' },
  { value: 'over-12-months', label: 'Over 12 months' },
];

const TEAM_SIZES = [
  { value: 'solo', label: 'Solo Consultant' },
  { value: '2-3', label: '2-3 People' },
  { value: '4-6', label: '4-6 People' },
  { value: '7-10', label: '7-10 People' },
  { value: 'over-10', label: 'Over 10 People' },
];

export function AIQuickSOW({ accessToken, consultingFirm, onClose }: AIQuickSOWProps) {
  const [step, setStep] = useState<'input' | 'generating' | 'preview' | 'edit'>('input');
  const [formData, setFormData] = useState<QuickSOWInput>({
    clientName: '',
    industry: '',
    projectType: '',
    budgetRange: '',
    additionalContext: '',
    timeline: '2-3-months',
    teamSize: 'solo',
  });
  const [generatedSOW, setGeneratedSOW] = useState<GeneratedSOW | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Template suggestion state
  const [engagementTypes, setEngagementTypes] = useState<any[]>([]);
  const [sowTemplates, setSOWTemplates] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedEngagementType, setSelectedEngagementType] = useState<string>('');
  const [suggestedTemplates, setSuggestedTemplates] = useState<any[]>([]);

  // Load engagement types and templates on mount
  useEffect(() => {
    if (consultingFirm) {
      loadEngagementTypes();
      loadSOWTemplates();
    }
  }, [consultingFirm]);

  // Filter templates when engagement type changes
  useEffect(() => {
    if (selectedEngagementType) {
      const filtered = sowTemplates.filter(
        template => template.engagementTypeId === selectedEngagementType
      );
      setSuggestedTemplates(filtered);
    } else {
      setSuggestedTemplates([]);
    }
  }, [selectedEngagementType, sowTemplates]);

  const loadEngagementTypes = async () => {
    if (!consultingFirm?.id && !consultingFirm?.firmId) return;
    
    try {
      setLoadingTypes(true);
      const firmId = consultingFirm?.id || consultingFirm?.firmId;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEngagementTypes((data.engagementTypes || []).filter((type: any) => type !== null && type !== undefined));
      }
    } catch (error) {
      console.error('[AI Quick SOW] Error loading engagement types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadSOWTemplates = async () => {
    if (!consultingFirm?.id && !consultingFirm?.firmId) return;
    
    try {
      setLoadingTemplates(true);
      const firmId = consultingFirm?.id || consultingFirm?.firmId;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSOWTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('[AI Quick SOW] Error loading SOW templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const applyTemplate = (template: any) => {
    // Map template budget range to form budget range
    const mapBudgetRange = (budgetStr: string) => {
      if (!budgetStr) return '';
      if (budgetStr.includes('25') && budgetStr.includes('50')) return '25k-50k';
      if (budgetStr.includes('50') && budgetStr.includes('100')) return '50k-100k';
      if (budgetStr.includes('100') && budgetStr.includes('250')) return '100k-250k';
      if (budgetStr.includes('250') && budgetStr.includes('500')) return '250k-500k';
      if (budgetStr.includes('500')) return 'over-500k';
      return 'under-25k';
    };

    // Map estimated hours to timeline
    const mapHoursToTimeline = (hours: number) => {
      if (hours < 80) return '2-4-weeks';
      if (hours < 160) return '1-2-months';
      if (hours < 320) return '2-3-months';
      if (hours < 640) return '3-6-months';
      if (hours < 1280) return '6-12-months';
      return 'over-12-months';
    };

    // Pre-fill form with template data
    setFormData(prev => ({
      ...prev,
      budgetRange: template.budgetRange ? mapBudgetRange(template.budgetRange) : prev.budgetRange,
      timeline: template.estimatedHours ? mapHoursToTimeline(template.estimatedHours) : prev.timeline,
      additionalContext: [
        prev.additionalContext,
        template.scope ? `\n\n**Template Scope:**\n${template.scope}` : '',
        template.deliverables?.length > 0 ? `\n\n**Key Deliverables:**\n• ${template.deliverables.join('\n• ')}` : '',
        template.assumptions?.length > 0 ? `\n\n**Assumptions:**\n• ${template.assumptions.join('\n• ')}` : '',
      ].filter(Boolean).join(''),
    }));

    toast.success(`Applied template: "${template.name}". AI will use this as a foundation.`);
  };

  const isFormValid = () => {
    return (
      formData.clientName.trim() !== '' &&
      formData.industry !== '' &&
      formData.projectType !== '' &&
      formData.budgetRange !== ''
    );
  };

  const handleGenerate = async () => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setGenerating(true);
    setStep('generating');
    setGenerationProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setGenerationProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/generate-quick-sow`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            input: formData,
            consultingFirm,
          }),
        }
      );

      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (response.ok) {
        const data = await response.json();
        setGeneratedSOW(data.sow);
        setTimeout(() => {
          setStep('preview');
          toast.success('SOW generated successfully');
        }, 500);
      } else {
        const error = await response.json();
        console.error('Error generating SOW:', error);
        toast.error(error.error || 'Failed to generate SOW');
        setStep('input');
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Error generating SOW:', error);
      toast.error('Failed to generate SOW');
      setStep('input');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedSOW) return;

    setSaving(true);
    try {
      const firmId = consultingFirm?.id || consultingFirm?.firmId;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sows`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            clientName: formData.clientName,
            projectTitle: generatedSOW.title,
            scope: generatedSOW.scope,
            deliverables: generatedSOW.deliverables,
            timeline: generatedSOW.timeline,
            pricing: generatedSOW.pricing,
            assumptions: generatedSOW.assumptions,
            exclusions: generatedSOW.exclusions,
            paymentTerms: generatedSOW.paymentTerms,
            status: 'draft',
          }),
        }
      );

      if (response.ok) {
        toast.success('SOW saved successfully');
        if (onClose) onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save SOW');
      }
    } catch (error) {
      console.error('Error saving SOW:', error);
      toast.error('Failed to save SOW');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!generatedSOW) return;

    setDownloading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sow/generate-pdf`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            sow: generatedSOW,
            consultingFirm,
            clientName: formData.clientName,
          }),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${formData.clientName.replace(/\s+/g, '_')}_SOW.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF downloaded successfully');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between pb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
              <Wand2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl">AI-Powered Quick SOW</h1>
              <p className="text-sm text-muted-foreground">Generate a professional Statement of Work in under 2 minutes</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {step === 'preview' && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Generated
            </Badge>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        <div className={`flex items-center gap-2 ${step === 'input' ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${step === 'input' ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
            1
          </div>
          <span className="text-sm">Input</span>
        </div>
        <div className="h-px w-12 bg-gray-300" />
        <div className={`flex items-center gap-2 ${step === 'generating' ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${step === 'generating' ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
            {step === 'generating' ? <Loader2 className="h-4 w-4 animate-spin" /> : '2'}
          </div>
          <span className="text-sm">AI Generation</span>
        </div>
        <div className="h-px w-12 bg-gray-300" />
        <div className={`flex items-center gap-2 ${step === 'preview' || step === 'edit' ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${step === 'preview' || step === 'edit' ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
            3
          </div>
          <span className="text-sm">Preview & Export</span>
        </div>
      </div>

      {/* Input Step */}
      {step === 'input' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Quick Setup
            </CardTitle>
            <CardDescription>
              Answer a few questions and let AI generate a comprehensive SOW
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Template Suggestions Panel */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-5 -mt-2">
              <div className="flex items-start gap-3 mb-4">
                <Sparkles className="size-6 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-900 mb-1">
                    Fast Track: Start with a Template (Optional)
                  </h3>
                  <p className="text-sm text-purple-700">
                    Select an engagement type to see pre-built SOW templates that AI can use as a foundation
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Select value={selectedEngagementType} onValueChange={setSelectedEngagementType}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder={loadingTypes ? "Loading..." : "Select engagement type..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {engagementTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div>
                          <div className="font-medium">{type.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {type.estimatedHours}h • {type.standardDurationWeeks} weeks
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Template Suggestions */}
                {selectedEngagementType && suggestedTemplates.length > 0 && (
                  <div className="mt-3 bg-gradient-to-r from-purple-50 to-yellow-50 border-2 border-purple-400 rounded-lg p-4 animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="size-5 text-purple-600" />
                      <h4 className="font-semibold text-purple-900">
                        Available Templates ({suggestedTemplates.length})
                      </h4>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {suggestedTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="bg-white border border-purple-200 rounded-lg p-3 hover:border-purple-400 hover:shadow-md transition-all"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <h5 className="font-semibold text-gray-900 truncate">
                                {template.name}
                              </h5>
                              <p className="text-xs text-gray-600 line-clamp-1">
                                {template.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-2">
                            {template.estimatedHours && (
                              <div className="flex items-center gap-1">
                                <Clock className="size-3" />
                                <span>{template.estimatedHours}h</span>
                              </div>
                            )}
                            {template.budgetRange && (
                              <div className="flex items-center gap-1">
                                <DollarSign className="size-3" />
                                <span>{template.budgetRange}</span>
                              </div>
                            )}
                            {template.usageCount !== undefined && (
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="size-3 text-green-600" />
                                <span>Used {template.usageCount}x</span>
                              </div>
                            )}
                          </div>

                          <Button
                            onClick={() => applyTemplate(template)}
                            size="sm"
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            <Sparkles className="size-3 mr-1" />
                            Use as Foundation for AI
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No templates state */}
                {selectedEngagementType && suggestedTemplates.length === 0 && !loadingTemplates && (
                  <div className="mt-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3 text-center">
                    <Package className="size-6 mx-auto mb-1 text-gray-400" />
                    <p className="text-xs text-gray-600">
                      No templates for this engagement type yet
                    </p>
                  </div>
                )}
              </div>
            </div>

            <Separator className="my-6" />

            {/* Client Name */}
            <div className="space-y-2">
              <Label htmlFor="clientName">
                Client Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="clientName"
                placeholder="e.g., Acme Corporation"
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
              />
            </div>

            {/* Industry */}
            <div className="space-y-2">
              <Label htmlFor="industry">
                Industry <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                <SelectTrigger id="industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((industry) => (
                    <SelectItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Type */}
            <div className="space-y-2">
              <Label htmlFor="projectType">
                Project Type <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.projectType} onValueChange={(value) => setFormData({ ...formData, projectType: value })}>
                <SelectTrigger id="projectType">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}</SelectContent>
              </Select>
            </div>

            {/* Budget Range */}
            <div className="space-y-2">
              <Label htmlFor="budgetRange">
                Budget Range <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.budgetRange} onValueChange={(value) => setFormData({ ...formData, budgetRange: value })}>
                <SelectTrigger id="budgetRange">
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timeline */}
            <div className="space-y-2">
              <Label htmlFor="timeline">Estimated Timeline</Label>
              <Select value={formData.timeline} onValueChange={(value) => setFormData({ ...formData, timeline: value })}>
                <SelectTrigger id="timeline">
                  <SelectValue placeholder="Select timeline" />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINES.map((timeline) => (
                    <SelectItem key={timeline.value} value={timeline.value}>
                      {timeline.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Team Size */}
            <div className="space-y-2">
              <Label htmlFor="teamSize">Team Size</Label>
              <Select value={formData.teamSize} onValueChange={(value) => setFormData({ ...formData, teamSize: value })}>
                <SelectTrigger id="teamSize">
                  <SelectValue placeholder="Select team size" />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_SIZES.map((size) => (
                    <SelectItem key={size.value} value={size.value}>
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Additional Context */}
            <div className="space-y-2">
              <Label htmlFor="additionalContext">
                Additional Context <span className="text-muted-foreground text-sm">(Optional)</span>
              </Label>
              <Textarea
                id="additionalContext"
                placeholder="Any specific requirements, challenges, or goals for this project..."
                rows={6}
                value={formData.additionalContext}
                onChange={(e) => setFormData({ ...formData, additionalContext: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Tip: Add details about specific pain points, integrations needed, or success criteria
              </p>
            </div>

            {/* Generate Button */}
            <div className="flex items-center gap-3 pt-4">
              <Button
                onClick={handleGenerate}
                disabled={!isFormValid() || generating}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate SOW with AI
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <Alert className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
              <Zap className="h-4 w-4 text-purple-600" />
              <AlertDescription className="text-purple-900">
                <strong>AI Magic:</strong> Our AI will analyze your inputs and generate a comprehensive SOW including scope, deliverables, timeline, pricing, assumptions, exclusions, and risk mitigation strategies.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Generating Step - (existing code continues...) */}
      {step === 'generating' && (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center gap-6 max-w-md mx-auto text-center">
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
                  <Wand2 className="h-10 w-10 text-white" />
                </div>
                <div className="absolute -inset-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full opacity-20 animate-ping" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-xl">AI is generating your SOW...</h3>
                <p className="text-muted-foreground">
                  Analyzing industry best practices and creating comprehensive documentation
                </p>
              </div>

              <div className="w-full space-y-2">
                <Progress value={generationProgress} className="h-2" />
                <p className="text-sm text-muted-foreground">{generationProgress}% complete</p>
              </div>

              <div className="grid grid-cols-2 gap-4 w-full text-left text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className={`h-4 w-4 mt-0.5 ${generationProgress > 20 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className={generationProgress > 20 ? 'text-foreground' : 'text-muted-foreground'}>
                    Analyzing project scope
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className={`h-4 w-4 mt-0.5 ${generationProgress > 40 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className={generationProgress > 40 ? 'text-foreground' : 'text-muted-foreground'}>
                    Generating deliverables
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className={`h-4 w-4 mt-0.5 ${generationProgress > 60 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className={generationProgress > 60 ? 'text-foreground' : 'text-muted-foreground'}>
                    Calculating pricing
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className={`h-4 w-4 mt-0.5 ${generationProgress > 80 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className={generationProgress > 80 ? 'text-foreground' : 'text-muted-foreground'}>
                    Creating timeline
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Step - Placeholder for brevity */}
      {step === 'preview' && generatedSOW && (
        <div className="space-y-6">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>SOW Generated Successfully</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setStep('input')}>
                    <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                    Start Over
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} disabled={downloading}>
                    {downloading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save SOW
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>{generatedSOW.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{generatedSOW.executiveSummary}</p>
              {/* Full preview content can be added here */}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}